// import { ProductType } from 'components/shared';
import { API_BASE_URL, WEBCMS_API_BASE_URL } from './configs';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from './vendor/shopifyHelper';

// export type AffiliateResponse = zod.infer<typeof affiliateResponseSchema>;

export const getAffiliateMetadata = async (affiliateCode: string) => {
  try {
    const url = `${API_BASE_URL}/api/web_v1/affiliate_partner/${affiliateCode}/`;
    // Affiliate-code-keyed config (not user-specific) — cache per code.
    const res = await fetch(url, { next: { revalidate: 300 } });
    return await res.json();
  } catch (err) {
    console.log({ err });
  }
};

export type AffiliateDiscountType = 'percentage' | 'amount';

export type AffiliateDiscountConfig = {
  code: string;
  type: AffiliateDiscountType;
  value: number;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- consumed as a type via `typeof AFFILIATE_DISCOUNT_PRODUCT_TYPES`
const AFFILIATE_DISCOUNT_PRODUCT_TYPES = [
  'RING',
  'RING_RARE',
  'RING_DIESEL',
  'M1',
  'HOME',
  'BLOOD_VISION',
  'PERFORMANCE_LABS',
];

/**
 * One Performance Labs product's discount. Keyed in the API by its Shopify
 * variant id — the same id `plans.ts` carries on every plan and posts on
 * add-to-cart, which is what makes the two sides joinable.
 */
export type PerformanceLabsProductDiscount = AffiliateDiscountConfig & {
  slug: string;
  name: string;
};

/**
 * Performance Labs is a catalogue (~35 products), not a single SKU like ring or
 * m1, so its affiliate discounts are published per region as either:
 *   - `default`  — one discount covering the whole catalogue, or
 *   - `products` — a per-variant map, when ops scoped the code to some products.
 *
 * Resolve a plan as `products[variantId] ?? default ?? none`. The catalogue-wide
 * case is deliberately NOT expanded into every variant server-side, so a plan
 * on the site whose backend Product row does not exist yet still picks up a
 * blanket discount.
 */
export type PerformanceLabsDiscountConfig = {
  default: AffiliateDiscountConfig | null;
  products: Record<string, PerformanceLabsProductDiscount>;
};

export type AffiliateDetails = {
  name: string;
  code: string;
  image?: string;
  m1Active: boolean;
  ringActive: boolean;
  ringProActive: boolean;
  ringRareActive: boolean;
  ringDieselActive: boolean;
  homeActive: boolean;
  bloodVisionActive: boolean;
  performanceLabsActive: boolean;
  subtitle: string;
  landing_page: string;
  discount_configs: {
    [key in ShopifyStore]?: {
      [key in (typeof AFFILIATE_DISCOUNT_PRODUCT_TYPES)[number]]?: AffiliateDiscountConfig;
    };
  };
  performance_labs_discount_configs: {
    [key in ShopifyStore]?: PerformanceLabsDiscountConfig;
  };
};

export const recordAffiliateLead = async (
  affiliateCode: string,
  leadEmail: string,
): Promise<any> => {
  try {
    const params = { affiliate: affiliateCode, email: leadEmail, plan: '' };
    const result = await fetch(`${API_BASE_URL}/referral`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return result;
  } catch (err) {
    return Promise.reject(err);
  }
};

// -=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-

export type PartnerElementData = {
  name: string;
  code: string;
  image: string | null;
  subtitle: string | null;
  affiliate_code: string;
  email_template: string;
  title: string;
  cta_title: string;
  m1_active: boolean;
  ring_active: boolean;
  brandColor: string;
  authentication_type: 'google' | 'email';
};

// export const searchWorkAffiliate = async (input: string) => {
//   const searchTerm = input.toLowerCase();
//   const fetchUrl = `/api/affiliate/work/?q=${searchTerm}`;
//   const res = await fetch(fetchUrl, { method: 'GET' })
//     .then((res) => res.json())
//     .catch(() => null);
//   return (await res) ?? null;
// };

export const getAffiliateDiscount = async (
  store: ShopifyStore,
  affiliateCode: string,
) => {
  const fetchUrl = `/api/affiliate/discount/`;
  const res = await fetch(fetchUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ store, affiliateCode }),
  })
    .then((res) => res.json())
    .catch(() => null);
  return (await res) ?? null;
};

// export const getWorkAffiliate = async (code: string) => {
//   const fetchUrl = `/api/affiliate/work/?code=${code.toLowerCase()}`;
//   const res = await fetch(fetchUrl, { method: 'GET' })
//     .then((res) => res.json())
//     .catch((e) => {
//       console.log(e);
//       return null;
//     });
//   console.log('🥹🥹🥹🥹🥹', res);
//   return res ?? null;
// };

export const searchWorkAffiliate = async (input: string) => {
  const searchTerm = input.toLowerCase();
  const fetchUrl =
    WEBCMS_API_BASE_URL + `/ultrawork/?search=${searchTerm}&page=1&limit=5`;
  try {
    const res = await fetch(fetchUrl, { method: 'GET' });
    if (res.status === 404) {
      return null;
    }
    const data = await res.json();
    if (data.success) {
      return data;
    }
    return null;
  } catch (e) {
    console.log(e);
    return null;
  }
};

export const getWorkAffiliate = async (
  code: string,
  store?: ShopifyStore | null,
) => {
  const fetchUrl =
    WEBCMS_API_BASE_URL + `/ultrawork/getCompany/${code.toLowerCase()}`;
  try {
    // Company-code-keyed config (not user-specific) — cache per code.
    const res = await fetch(fetchUrl, {
      method: 'GET',
      next: { revalidate: 300 },
    });
    if (res.status === 404) {
      return null;
    }
    const data = await res.json();

    if (data.success && 'data' in data) {
      // If store is provided and data has store-specific discounts, apply store alias mapping
      if (store && data.data.discounts_by_store) {
        const effectiveStore = getEffectiveStoreForDiscount(store);
        // Return data with mapped store discounts if available
        if (effectiveStore && data.data.discounts_by_store[effectiveStore]) {
          return {
            ...data.data,
            discounts: data.data.discounts_by_store[effectiveStore],
          };
        }
      }
      return data.data;
    }
    return null;
  } catch (e) {
    console.log(e);
    return null;
  }
};

export type UltraworkMagicLinkResponse = {
  statusCode: number;
  status: string;
  message: string;
  data?: {
    email: string;
    companyCode: string;
    // Only present on the /verify response. Send it as the
    // x-ultrawork-session header to getWorkAffiliateDiscounts below.
    sessionToken?: string;
  };
};

export type WorkAffiliateDiscounts = {
  id: number;
  ultrawork_code: string;
  ring_discount: number | null;
  ring_discount_code: string | null;
  m1_discount: number | null;
  m1_discount_code: string | null;
  ring_active?: boolean;
  m1_active?: boolean;
};

// 'session_invalid' is a 401 (expired or missing token) so the caller can
// re-prompt. A 403 is a different company than the token was minted for --
// treating that as "please sign in again" would email a new link on every
// fetch. 'not_found' and 'error' stay separate for the same reason.
export type WorkAffiliateDiscountsResult =
  | { status: 'ok'; discounts: WorkAffiliateDiscounts }
  | { status: 'session_invalid' }
  | { status: 'forbidden' }
  | { status: 'not_found' }
  | { status: 'error' };

const ULTRAWORK_SESSION_KEY = 'uh_ultrawork_session';

export type UltraworkStoredSession = {
  token: string;
  companyCode: string;
};

export function readUltraworkSession(): UltraworkStoredSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(ULTRAWORK_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UltraworkStoredSession;
    if (
      !parsed ||
      typeof parsed.token !== 'string' ||
      typeof parsed.companyCode !== 'string'
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeUltraworkSession(session: UltraworkStoredSession): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(ULTRAWORK_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Private mode or a full quota. The in-memory token still works on this page.
  }
}

export function clearUltraworkSession(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(ULTRAWORK_SESSION_KEY);
  } catch {
    // Ignore. The caller also drops the in-memory token.
  }
}

/**
 * Fetches a company's live discount codes. Requires the sessionToken returned
 * by verifyUltraworkToken -- the backend rejects this call without one
 * (ULT-257 / ULT-260: this used to be part of the public getWorkAffiliate
 * response, readable with no proof of work-email ownership).
 */
export const getWorkAffiliateDiscounts = async (
  code: string,
  sessionToken: string,
): Promise<WorkAffiliateDiscountsResult> => {
  try {
    // The session token is scoped to the company code exactly as verify
    // stored it. Lowercasing here 403s any code that isn't already lowercase.
    const fetchUrl =
      WEBCMS_API_BASE_URL +
      `/ultrawork/getCompany/${encodeURIComponent(code)}/discounts`;
    const res = await fetch(fetchUrl, {
      method: 'GET',
      headers: { 'x-ultrawork-session': sessionToken },
    });

    if (res.status === 401) {
      return { status: 'session_invalid' };
    }
    if (res.status === 403) {
      return { status: 'forbidden' };
    }
    if (res.status === 404) {
      return { status: 'not_found' };
    }
    if (!res.ok) {
      return { status: 'error' };
    }

    const data = await res.json();
    if (!data?.success || data.data?.discounts == null) return { status: 'error' };
    return { status: 'ok', discounts: data.data.discounts };
  } catch (e) {
    console.log(e);
    return { status: 'error' };
  }
};

export const generateUltraworkMagicLink = async (
  email: string,
  companyCode: string,
): Promise<UltraworkMagicLinkResponse> => {
  try {
    const response = await fetch(`${WEBCMS_API_BASE_URL}/ultrawork/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, companyCode }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error generating magic link:', error);
    return {
      statusCode: 500,
      status: 'fail',
      message: 'Error generating magic link',
    };
  }
};

export const verifyUltraworkToken = async (
  token: string,
  email: string,
  companyCode: string,
): Promise<UltraworkMagicLinkResponse> => {
  try {
    const response = await fetch(`${WEBCMS_API_BASE_URL}/ultrawork/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, email, companyCode }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error verifying token:', error);
    return {
      statusCode: 500,
      status: 'fail',
      message: 'Error verifying token',
    };
  }
};
